import React, { useState, useMemo } from 'react';
import { graphql } from 'gatsby';
import { useForm } from 'react-hook-form';
import WithSection from '../../components/WithSection/WithSection';
import Box from '../../components/Box/Box';
import Button from '../../components/Button/Button';
import InputSlots, { normalizeValues } from '../../slots/InputSlots/InputSlots';
import { submitForm } from '../../services/externalForm';
import { Body1, Head3 } from '../../components/Typography/Typography';
import Image from '../../components/Image/Image';
import CheckMarkIcon from '../../images/icons/check-mark.svg';
import useViewport from '../../hooks/useViewport';

const FormSection: React.FC = ({ data }) => {
    const {
        primary: { heading, subheading, image, form },
    } = data;
    const { submit_url, cta_text, success_msg, fields } = form.document.data;
    const { isMobile } = useViewport();
    const { handleSubmit, register, errors, reset, control } = useForm();
    const [submitted, setSubmitted] = useState(false);

    const defaultOptions = useMemo(
        () =>
            fields.reduce((defaultObject, currInput) => {
                defaultObject[currInput.key] = '';
                return defaultObject;
            }, {}),
        [fields]
    );

    const onSubmit = async (values) => {
        const normalizedValues = normalizeValues(values);
        reset(defaultOptions);
        await submitForm({ url: submit_url, data: normalizedValues });
        setSubmitted(true);
    };

    return (
        !isMobile && (
            <Box gridColumn={{ _: '1/-1', lg: '2/-2', xl: '3/-3' }}>
                <Head3 bold textAlign={'center'}>
                    {heading.text}
                </Head3>
                {subheading && (
                    <Body1 mt="20px" textAlign={'center'}>
                        {subheading}
                    </Body1>
                )}
                <Box
                    display="grid"
                    gridRowGap="60px"
                    gridColumnGap="40px"
                    alignItems="flex-start"
                    gridTemplateColumns={{
                        sm: '1fr 1fr',
                    }}
                    paddingTop="75px"
                >
                    <Box display="contents">
                        <Image img={image} />
                    </Box>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Box marginBottom="28px">
                            <InputSlots
                                inputs={fields}
                                extendProps={({
                                    key,
                                    is_required,
                                    pattern,
                                    label,
                                }) => ({
                                    ref: register({
                                        required: is_required,
                                        pattern,
                                    }),
                                    error: errors[key],
                                    control,
                                    label,
                                })}
                            />
                        </Box>
                        <Button>{cta_text}</Button>
                        {submitted && (
                            <Box
                                marginTop="28px"
                                bg="success.100"
                                padding="8px 44px"
                                color="success.1700"
                                display="flex"
                                css={css`
                                    gap: 12px;
                                `}
                            >
                                <CheckMarkIcon />
                                <Body1 color="success.1700">
                                    {success_msg}
                                </Body1>
                            </Box>
                        )}
                    </form>
                </Box>
            </Box>
        )
    );
};

export const _ = graphql`
    fragment FORM_SECTION on PrismicSectionDataBodyFormSection {
        id
        primary {
            heading {
                text
            }
            image {
                gatsbyImageData(height: 306, layout: CONSTRAINED)
            }
            subheading
            form {
                type
                id
                document {
                    ... on PrismicDynamicForm {
                        id
                        data {
                            cta_text
                            fields {
                                key
                                is_required
                                label
                                options
                                placeholder
                                type
                            }
                            submit_url
                            success_msg
                        }
                    }
                }
            }
        }
    }
`;

export default WithSection(FormSection);
